import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { PlusCircle, Trash2 } from "react-feather";
import toast from "react-hot-toast";
import ProductPicker from "../../../stock/components/ProductPicker";
import { useUpdateSurveyMutation } from "../surveysApi";

const MAX_LINES = 200;
const blankLine = () => ({
  key: `l-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  productCode: "",
  productBranchId: null,
  description: "",
  unit: "",
  qty: "",
  remarks: "",
});

const fromDetail = (s) => ({
  contactPerson: s.contactPerson || "",
  contactDesignation: s.contactDesignation || "",
  contactMobile: s.contactMobile || "",
  siteAddress: s.siteAddress || "",
  requirements: s.requirements || "",
  remarks: s.remarks || "",
  lines: (s.lines || []).map((l, i) => ({
    key: `d-${i}-${l.productCode}`,
    productCode: l.productCode,
    productBranchId: l.productBranchId ?? null,
    description: l.description || "",
    unit: l.unit || "",
    qty: String(l.qty ?? ""),
    remarks: l.remarks || "",
  })),
  // Batch224 - the service template's group lines: qty can change or the line be removed,
  // no new groups (user, 2026-11-03).
  groupLines: (s.groupLines || []).map((g) => ({
    key: `g-${g.lineNumber}-${g.productGroupId}`,
    lineNumber: g.lineNumber,
    productGroupId: g.productGroupId,
    description: g.description || g.groupName || "",
    groupName: g.groupName || "",
    unit: g.unit || "",
    qty: String(g.qty ?? ""),
  })),
});

const blankToNull = (v) => (v && String(v).trim() ? String(v).trim() : null);
const TWO_DECIMALS = /^\d+(\.\d{1,2})?$/;

/**
 * Edit a survey's contact/site/requirements/remarks and its materials. Customer or
 * prospect, branch, service (prototype) and GPS are kept exactly as loaded - the
 * PUT replaces the whole record, so they are sent back unchanged.
 */
const SurveyEditModal = ({ show, survey, onClose }) => {
  const [form, setForm] = useState(() => fromDetail(survey));
  const [updateSurvey, { isLoading }] = useUpdateSurveyMutation();

  useEffect(() => {
    if (show) setForm(fromDetail(survey));
  }, [show, survey]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setLine = (key, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }));
  const removeLine = (key) => setForm((f) => ({ ...f, lines: f.lines.filter((l) => l.key !== key) }));
  const setGroupQty = (key, qty) => setForm((f) => ({ ...f, groupLines: f.groupLines.map((g) => (g.key === key ? { ...g, qty } : g)) }));
  const removeGroup = (key) => setForm((f) => ({ ...f, groupLines: f.groupLines.filter((g) => g.key !== key) }));

  const handleSave = async () => {
    const { lines, groupLines } = form;
    if (lines.length + groupLines.length > MAX_LINES) return toast.error(`At most ${MAX_LINES} materials`);
    for (let i = 0; i < lines.length; i += 1) {
      const l = lines[i];
      if (!l.productCode) return toast.error(`Line ${i + 1}: pick a product`);
      if (!TWO_DECIMALS.test(String(l.qty).trim()) || Number(l.qty) <= 0) {
        return toast.error(`Line ${i + 1}: quantity must be more than 0 (up to 2 decimals)`);
      }
    }
    for (const g of groupLines) {
      if (!TWO_DECIMALS.test(String(g.qty).trim()) || Number(g.qty) <= 0) {
        return toast.error(`${g.description}: quantity must be more than 0 (up to 2 decimals)`);
      }
    }
    if (survey.status === "COMPLETED" && lines.length + groupLines.length === 0) {
      return toast.error("A completed survey needs at least one material");
    }

    try {
      await updateSurvey({
        recordGuid: survey.recordGuid,
        branchId: survey.branchId,
        prototypeRecordGuid: survey.prototypeRecordGuid || null,
        custId: survey.isProspect ? null : survey.custId ?? null,
        customerBranchId: survey.isProspect ? null : survey.customerBranchId ?? null,
        locationId: survey.locationId ?? null,
        prospectName: survey.isProspect ? survey.prospectName : null,
        prospectPhone: survey.isProspect ? survey.prospectPhone : null,
        prospectArea: survey.isProspect ? survey.prospectArea : null,
        contactPerson: blankToNull(form.contactPerson),
        contactDesignation: blankToNull(form.contactDesignation),
        contactMobile: blankToNull(form.contactMobile),
        siteAddress: blankToNull(form.siteAddress),
        requirements: blankToNull(form.requirements),
        remarks: blankToNull(form.remarks),
        latitude: survey.latitude ?? null,
        longitude: survey.longitude ?? null,
        accuracyMeters: survey.accuracyMeters ?? null,
        lines: lines.map((l) => ({
          productCode: Number(l.productCode),
          productBranchId: l.productBranchId ?? survey.branchId,
          qty: Number(l.qty),
          remarks: blankToNull(l.remarks),
        })),
        groupLines: groupLines.map((g) => ({
          productGroupId: Number(g.productGroupId),
          lineNumber: g.lineNumber,
          qty: Number(g.qty),
        })),
      }).unwrap();
      toast.success("Survey saved");
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not save the survey");
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" scrollable backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h6">Edit survey {survey.surveyNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          {survey.isProspect ? survey.prospectName : survey.customerName} · {survey.serviceName || "no service"} · {survey.branchCode}
          <span className="ms-1">(customer, service and branch are not changed here)</span>
        </div>
        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">Contact person</label>
            <input className="form-control" value={form.contactPerson} onChange={set("contactPerson")} maxLength={100} />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Designation</label>
            <input className="form-control" value={form.contactDesignation} onChange={set("contactDesignation")} maxLength={100} />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Mobile</label>
            <input className="form-control" value={form.contactMobile} onChange={set("contactMobile")} maxLength={30} />
          </div>
          <div className="col-12 mb-3">
            <label className="form-label">Site address</label>
            <input className="form-control" value={form.siteAddress} onChange={set("siteAddress")} />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Requirements</label>
            <textarea className="form-control" rows={3} value={form.requirements} onChange={set("requirements")} />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Remarks</label>
            <textarea className="form-control" rows={3} value={form.remarks} onChange={set("remarks")} />
          </div>
        </div>

        <h6 className="mb-2">Materials</h6>
        <div className="table-responsive">
          <table className="table table-bordered align-middle" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ minWidth: 280 }}>Product</th>
                <th style={{ width: 80 }}>Unit</th>
                <th style={{ width: 110 }}>Qty</th>
                <th style={{ minWidth: 180 }}>Remarks</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {form.lines.map((l, idx) => (
                <tr key={l.key}>
                  <td>{idx + 1}</td>
                  <td>
                    <ProductPicker
                      branchId={survey.branchId}
                      value={l.productCode ? { value: l.productCode, label: l.description || `Product ${l.productCode}` } : null}
                      onChange={(opt) =>
                        setLine(l.key, {
                          productCode: opt?.value ?? "",
                          productBranchId: opt?.product?.branchId ?? null,
                          description: opt?.product?.productName || "",
                          unit: opt?.product?.unitLabel || "",
                        })
                      }
                    />
                  </td>
                  <td className="small">{l.unit || "-"}</td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={l.qty}
                      onChange={(e) => setLine(l.key, { qty: e.target.value })}
                    />
                  </td>
                  <td>
                    <input className="form-control" value={l.remarks} onChange={(e) => setLine(l.key, { remarks: e.target.value })} />
                  </td>
                  <td>
                    <button type="button" className="btn btn-link text-danger p-0" title="Remove" onClick={() => removeLine(l.key)}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={addLine} disabled={form.lines.length >= MAX_LINES}>
          <PlusCircle size={14} className="me-1" /> Add material
        </button>

        {form.groupLines.length > 0 && (
          <>
            <h6 className="mt-4 mb-1">Product groups</h6>
            <div className="small text-muted mb-2">From the service template - change the quantity or remove a line.</div>
            <div className="table-responsive">
              <table className="table table-bordered align-middle" style={{ minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: 280 }}>Group</th>
                    <th style={{ width: 80 }}>Unit</th>
                    <th style={{ width: 110 }}>Qty</th>
                    <th style={{ width: 44 }} />
                  </tr>
                </thead>
                <tbody>
                  {form.groupLines.map((g) => (
                    <tr key={g.key}>
                      <td>
                        <span className="fw-semibold">{g.description}</span>
                        {g.groupName && g.groupName !== g.description && <div className="small text-muted">Group: {g.groupName}</div>}
                      </td>
                      <td className="small">{g.unit || "-"}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control"
                          value={g.qty}
                          onChange={(e) => setGroupQty(g.key, e.target.value)}
                        />
                      </td>
                      <td>
                        <button type="button" className="btn btn-link text-danger p-0" title="Remove" onClick={() => removeGroup(g.key)}>
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={isLoading}>
          {isLoading ? "Saving..." : "Save Survey"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SurveyEditModal;
